import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash/';
import classNames from 'classnames';

import { Title, Box, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import Disclosure, { disclosureTypes } from '../../disclosure/disclosure';
import parseAwardsFormatting from '../lib/parse-awards-formatting';
import classColor from '../../../lib/parse-color';

import {
  boolCheckboxSpec,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
} from '~/client/lib/editing-configs';

import '../awards-hero.less';

const LegacyLayout = ({
  backgroundIcon,
  backgroundIconMobile,
  animatedIcon,
  heroType2021,
  awardsHeroType,
  preheader,
  textColor,
  subtitle,
  subtitleMobile,
  subtitleSize,
  disclosureType,
  disclosureOnBackground,
  title,
  size,
  selectTopLeftContent,
}) => {
  const backgroundIconSrcMap = {
    lg: backgroundIcon,
    md: backgroundIconMobile,
    sm: backgroundIconMobile,
  };

  const awardsBadgeLogo =
    'https://www.nerdwallet.com/cdn/NW-awards/2020/hero/NW-awards-badge.png';

  // 2020: if advertiserDisclosure or awardsLogo is present, apply top margin to hero title to vertically align hero content
  // 2021: apply bespoke title margins for each type of landing page, per viewport size
  const isAwardsLogo = selectTopLeftContent === 'awardsLogo';

  const typeOfMargin =
    isAwardsLogo || (heroType2021 && awardsHeroType === '2021LP2')
      ? '67px'
      : '30px';

  const mobileTypeOfMargin =
    isAwardsLogo || (heroType2021 && awardsHeroType !== '2021LP2')
      ? '67px'
      : '30px';

  const mobileTitleMarginRule =
    disclosureType !== 'none' || isAwardsLogo ? typeOfMargin : '0';

  // 2020: if advertiserDisclosure or awardsLogo is present, apply bottom margin to subtitle to vertically align hero content
  // 2021: apply bespoke subtitle margins for each type of landing page, per viewport size
  const heroTitleTopMarginRule = {
    lg: '0',
    md: '0',
    sm: mobileTitleMarginRule,
  };

  const subtitleSizeOptions = {
    2: 'default',
    3: 'large',
  };

  const mobileSubtitleMarginRule =
    disclosureType !== 'none' || isAwardsLogo ? mobileTypeOfMargin : '0';

  const heroSubtitleBottomMarginRule = {
    lg: '0',
    md: '0',
    sm: mobileSubtitleMarginRule,
  };

  // show non-NDS colors and bespoke margins per hero year and type
  const subtitleTextClass = heroType2021
    ? 'awards-2021-hero__subtitle'
    : 'standard-hero__subtitle';

  // determine css layout class per awards year, and if 2021 per page level
  const layout2021 = awardsHeroType === '2021LP1' ? 'lp1-2021' : 'lp2-2021';
  const layout = heroType2021 ? layout2021 : 'lp-2020';
  const subtitleSizeValue =
    size === 'sm' ? 'small' : subtitleSizeOptions[subtitleSize];
  const subtitleValue = size === 'sm' ? subtitleMobile : subtitle;
  return (
    <div className={classNames(['awards-hero__content', `${layout}`])}>
      {heroType2021 && awardsHeroType === '2021LP1' && backgroundIcon && (
        <div className="awards-hero__background">
          <Image
            src={`${backgroundIconSrcMap[size]}`}
            alt="Best Of Awards 2021"
            backgroundColor="transparent"
            className="awards-hero__background-icon"
          />
        </div>
      )}
      {heroType2021 && awardsHeroType === '2021LP1' && animatedIcon && (
        <div
          className="awards-hero__animated-icon"
          style={{
            backgroundImage: `url(${animatedIcon})`,
          }}
        />
      )}
      {isAwardsLogo && (
        <Image
          src={awardsBadgeLogo}
          alt="Best Of Awards 2020 badge"
          className="awards-hero__badge-icon"
          backgroundColor="transparent"
        />
      )}
      {selectTopLeftContent === 'advertiserDisclosure' &&
        disclosureType &&
        disclosureType !== 'none' && (
          <div className="awards-hero__advertiser-disclosure">
            <Disclosure
              type={disclosureType}
              onBackground={disclosureOnBackground}
            />
          </div>
        )}
      {heroType2021 && awardsHeroType === '2021LP1' && preheader && (
        <Box
          className={classNames([
            'awards-hero__preheader',
            classColor(textColor),
          ])}
        >
          <Text size="large">{preheader}</Text>
        </Box>
      )}
      <Title
        component="h1"
        className={classNames([
          classColor('green'),
          'awards-hero__title',
          `${layout}`,
        ])}
        style={{
          marginTop:
            heroType2021 && awardsHeroType === '2021LP1'
              ? '20px'
              : `${heroTitleTopMarginRule[size]}`,
        }}
      >
        {parseAwardsFormatting(title)}
      </Title>
      <Box
        className={classNames([
          subtitleTextClass,
          classColor(textColor),
          `${layout}`,
        ])}
      >
        <Text
          size={subtitleSizeValue}
          style={{
            marginBottom: `${heroSubtitleBottomMarginRule[size]}`,
          }}
        >
          {subtitleValue}
        </Text>
      </Box>
    </div>
  );
};

LegacyLayout.propTypes = {
  heroType2021: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['2', '3']),
  backgroundIcon: PropTypes.string,
  backgroundIconMobile: PropTypes.string,
  animatedIcon: PropTypes.string,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundSizeMobile: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  awardsHeroType: PropTypes.oneOf(['2021LP1', '2021LP2']),
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureOnBackground: PropTypes.bool,
  preheader: PropTypes.string,
  selectTopLeftContent: PropTypes.oneOf(['advertiserDisclosure', 'awardsLogo']),
  size: PropTypes.string,
  textColor: PropTypes.string,
  awardsYear: PropTypes.string,
};

LegacyLayout.getEditingConfig = (componentDocument) => {
  const show2021LayoutDropdown = _.get(
    componentDocument,
    'props.heroType2021',
    true
  )
    ? [
        stringSelectSpec('awardsHeroType', '2021 Awards Hero Type', [
          { label: '2021 LP1', value: '2021LP1' },
          { label: '2021 LP2', value: '2021LP2' },
        ]),
      ]
    : [''];

  const show2021LP1EditingConfig =
    _.get(componentDocument, 'props.heroType2021', true) &&
    _.get(componentDocument, 'props.awardsHeroType') === '2021LP1'
      ? [
          stringInputSpec('backgroundIcon', 'Background icon'),
          stringInputSpec(
            'backgroundIconMobile',
            'Background icon for mobile devices'
          ),
          stringInputSpec('animatedIcon', 'Animated Icon'),
          stringTextAreaSpec('preheader', 'Pre-header text'),
        ]
      : [''];
  return [
    boolCheckboxSpec('heroType2021', '2021 Awards hero?'),
    ...show2021LayoutDropdown,
    ...show2021LP1EditingConfig,
  ];
};

export default LegacyLayout;
